import { create } from 'zustand'
import { persist } from 'zustand/middleware'

type CalendarType = 'AD' | 'BS'
type Language = 'en' | 'ne'
export type Theme = 'light' | 'dark' | 'system'
export type KeyboardMode = 'en' | 'unicode'

interface UiState {
  sidebarCollapsed: boolean
  /** Open or closed state per sidebar group id. A missing id means "use the default". */
  sidebarGroups: Record<string, boolean>
  calendarType: CalendarType
  language: Language
  theme: Theme
  keyboardMode: KeyboardMode

  // Actions
  toggleSidebarCollapsed: () => void
  setSidebarGroup: (id: string, open: boolean) => void
  toggleCalendar: () => void
  setCalendarType: (type: CalendarType) => void
  setLanguage: (lang: Language) => void
  setTheme: (theme: Theme) => void
  toggleKeyboard: () => void
  setKeyboardMode: (mode: KeyboardMode) => void
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      sidebarGroups: {},
      calendarType: 'AD',
      language: 'en',
      theme: 'system',
      keyboardMode: 'en',

      setSidebarGroup: (id, open) => set((s) => (s.sidebarGroups[id] === open ? s : { sidebarGroups: { ...s.sidebarGroups, [id]: open } })),
      toggleSidebarCollapsed: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setCalendarType: (calendarType) => set({ calendarType }),
      toggleCalendar: () =>
        set((s) => ({ calendarType: s.calendarType === 'AD' ? 'BS' : 'AD' })),
      setLanguage: (lang) => {
        localStorage.setItem('kvbms_language', lang)
        set({ language: lang })
      },
      setTheme: (theme) => set({ theme }),
      toggleKeyboard: () =>
        set((s) => ({ keyboardMode: s.keyboardMode === 'en' ? 'unicode' : 'en' })),
      setKeyboardMode: (mode) => set({ keyboardMode: mode }),
    }),
    {
      name: 'kvbms_ui',
      // v0 stored 'light' by default without a working toggle; reset it to follow the OS.
      version: 1,
      migrate: (state, version) =>
        version < 1 ? { ...(state as UiState), theme: 'system' as Theme } : (state as UiState),
    }
  )
)
